/**
 * TransactionsApiClient
 * 
 * Handles transaction history, transfers, and transaction-related operations
 */

import { BaseApiClient } from './BaseApiClient';
import type { Transaction, TransferRequest } from '../../types/api/payment';

export class TransactionsApiClient extends BaseApiClient {
  /**
   * Get transaction history with enhanced filtering
   */
  async getTransactions(filters?: {
    page?: number;
    limit?: number; // Combined limit (backward compatibility, not recommended)
    pendingLimit?: number; // Number of pending transactions to return (recommended)
    completedLimit?: number; // Number of completed transactions to return (recommended)
    type?: string;
    status?: string;
    search?: string;
    userId?: string;  // Support filtering by user ID for admin views
    counterpartyId?: string;  // Filter transactions by counterparty/contact ID
  }): Promise<{ 
    transactions: Transaction[]; 
    total: number; 
    page: number; 
    totalPages: number;
    pending?: Transaction[];
    completed?: Transaction[];
    pendingCount?: number;
    completedCount?: number;
  }> {
    console.log('🔄 Using enhanced /api/v1/transactions endpoint with separate pending/completed lists');
    
    const params = new URLSearchParams();
    if (filters?.page) params.append('page', filters.page.toString());
    // Use separate limits if provided (recommended), otherwise fall back to combined limit
    if (filters?.pendingLimit) {
      params.append('pendingLimit', filters.pendingLimit.toString());
    }
    if (filters?.completedLimit) {
      params.append('completedLimit', filters.completedLimit.toString());
    }
    // Only use combined limit if separate limits are not provided (backward compatibility)
    if (!filters?.pendingLimit && !filters?.completedLimit && filters?.limit) {
      params.append('limit', filters.limit.toString());
    }
    if (filters?.type) params.append('type', filters.type);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.userId) params.append('userId', filters.userId);  // Pass userId to backend for filtering
    if (filters?.counterpartyId) params.append('counterpartyId', filters.counterpartyId);  // Filter by counterparty/contact ID
    
    const result = await this.get<{ 
      transactions: Transaction[];
      pending: Transaction[];
      completed: Transaction[];
      total: number;
      pendingCount: number;
      completedCount: number;
    }>(`/transactions?${params}`);
    
    console.log(`📊 Enhanced API Response: ${result.pendingCount || 0} active, ${result.completedCount || 0} completed`);
    
    // Use the enhanced format provided by the backend
    const transactions = result.transactions || [];
    const pendingTransactions = result.pending || [];
    const completedTransactions = result.completed || [];
    const total = result.total || transactions.length;
    
    const limit = filters?.limit || 20;
    const page = filters?.page || 1;
    
    return {
      transactions, // For backward compatibility
      pending: pendingTransactions,
      completed: completedTransactions,
      total,
      pendingCount: result.pendingCount || pendingTransactions.length,
      completedCount: result.completedCount || completedTransactions.length,
      page: page,
      totalPages: Math.ceil(total / limit)
    };
  }

  /**
   * Send peer-to-peer transfer
   */
  async sendTransfer(transferData: TransferRequest): Promise<{
    success: boolean;
    transactionId: string;
    transferId: string;
    fees: any;
    estimatedArrival: string;
  }> {
    // Map to existing backend endpoint for now
    // TODO: Implement proper peer-to-peer transfer endpoint in backend
    throw new Error('Peer-to-peer transfers not yet implemented in backend. Please use the existing token/QR code system for now.');
  }

  /**
   * Fetch multiple transactions by transaction IDs (batch endpoint)
   * 
   * @param transactionIds Array of transaction IDs (codes or MongoDB ObjectIds)
   * @param userId Optional: User ID (admin only - can fetch transactions for other users)
   * @returns Transactions found (regular users can only fetch transactions they were party to)
   */
  async getTransactionsBatch(
    transactionIds: string[],
    userId?: string
  ): Promise<{
    transactions: Transaction[];
    count: number;
    requested: number;
    found: number;
  }> {
    if (!transactionIds || transactionIds.length === 0) {
      return {
        transactions: [],
        count: 0,
        requested: 0,
        found: 0
      };
    }

    // Enforce batch size limit
    if (transactionIds.length > 100) {
      console.warn(`⚠️ [TransactionsApiClient] Batch size exceeds limit (100). Requested: ${transactionIds.length}. Using first 100.`);
      transactionIds = transactionIds.slice(0, 100);
    }

    const body: { transactionIds: string[]; userId?: string } = {
      transactionIds
    };

    if (userId) {
      body.userId = userId;
    }

    const result = await this.post<{
      transactions: Transaction[];
      count: number;
      requested: number;
      found: number;
    }>('/transactions/batch', body);

    console.log(`📦 [TransactionsApiClient] Batch fetch: requested ${result.requested}, found ${result.found}`);

    return result;
  }

  /**
   * Export transactions to CSV or PDF
   */
  async exportTransactions(format: 'csv' | 'pdf', filters?: {
    startDate?: string;
    endDate?: string;
    type?: string;
    status?: string;
  }): Promise<{ downloadUrl: string }> {
    const params = new URLSearchParams({ format });
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.type) params.append('type', filters.type);
    if (filters?.status) params.append('status', filters.status);
    
    return this.get<{ downloadUrl: string }>(`/transactions/export?${params}`);
  }

  /**
   * Get transaction details by ID
   */
  async getTransactionDetails(transactionId: string): Promise<Transaction> {
    return this.get<Transaction>(`/transactions/${transactionId}`);
  }

  /**
   * Cancel a pending transaction
   */
  async cancelTransaction(transactionId: string): Promise<{ success: boolean; message: string }> {
    return this.post<{ success: boolean; message: string }>(`/transactions/${transactionId}/cancel`);
  }

  /**
   * Retry a failed transaction
   */
  async retryTransaction(transactionId: string): Promise<{ success: boolean; message: string }> {
    return this.post<{ success: boolean; message: string }>(`/transactions/${transactionId}/retry`);
  }

  /**
   * Get token details
   */
  async getTokenDetails(tokenKey: string): Promise<any> {
    return this.get(`/token-details/${tokenKey}`);
  }
} 